import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Gift, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";

const STORAGE_KEY = "klrn_welcome_dismissed";
const SESSION_KEY = "klrn_welcome_seen_session";

const WelcomeRewardsPopup = () => {
  const [show, setShow] = useState(false);
  const [animate, setAnimate] = useState(false);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isFr = language === "fr";

  useEffect(() => {
    // Don't show if already a rewards member / signed-in client
    if (localStorage.getItem("portal_token") || localStorage.getItem("client_token")) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    // Oct 20 2026 · Karine: "a popup pops up as soon as you go on the
    // website… keeps popping up again and again". Dismissal now lasts 30
    // days (was forever but some browsers/in-app webviews drop storage),
    // we remember "shown this session" separately, and we only fire after
    // the visitor has actually looked around (scrolled) — never on arrival.
    if (dismissed) {
      if (dismissed === "1") { localStorage.setItem(STORAGE_KEY, String(Date.now())); return; }
      const ts = parseInt(dismissed, 10);
      if (!Number.isFinite(ts) || Date.now() - ts < 30 * 24 * 60 * 60 * 1000) return;
    }
    try { if (sessionStorage.getItem(SESSION_KEY)) return; } catch { /* noop */ }
    // BUG FIX (Jun 22, 2026 — Michelle Best & others texted Karine
    // because they "couldn't book"): this popup is a full-screen blocking
    // overlay that hits within 4 s of any page load — including booking
    // flows. New visitors see it before they can reach Book and abandon.
    // Suppress entirely on booking-intent URLs so the popup never steals
    // a client mid-booking.
    const path = (window.location.pathname || "").toLowerCase();
    const search = (window.location.search || "").toLowerCase();
    const isBookingIntent =
      path.startsWith("/book") ||
      path.startsWith("/laser-and-skin") ||
      path.startsWith("/treatments") ||
      path.startsWith("/by-concern") ||
      path.startsWith("/concerns") ||
      path.startsWith("/consultation") ||
      path.startsWith("/checkin") ||
      path.startsWith("/staff") ||
      path.startsWith("/np/join") ||
      path.startsWith("/admin") ||
      path.startsWith("/my-account") ||
      path.startsWith("/my-appointments") ||
      // BUG FIX (Feb 2026 — clients frozen on LMC page): the Last Minute
      // Club opens Radix Dialogs (`LastMinuteClubModal` + `ContactModal`)
      // which mark all siblings as inert. If this popup fires over them,
      // the X and backdrop become non-clickable and the user is trapped.
      // Solution — never fire on LMC-related surfaces in the first place.
      path.startsWith("/last-minute") ||
      path.startsWith("/lastminute") ||
      path.startsWith("/lmc") ||
      search.includes("provider=") ||
      search.includes("book=");
    if (isBookingIntent) return;
    // ALSO suppress on ?analysis=true — HomePage auto-opens the AI Skin
    // Analysis modal on this deep-link (used by the Skincare Boutique
    // "personalized recommendation" CTA + navbar analysis button). The
    // popup would otherwise render on top and freeze the whole flow.
    if (search.includes("analysis=")) return;
    // Also give the user real runway on the home page so they can scan
    // the page and hit Book before the popup. 4 s was way too aggressive.
    let armed = false;       // true once the visitor has really looked around
    let elapsed = false;     // true after the minimum dwell time
    let fired = false;
    const scrolledEnough = () => {
      const doc = document.documentElement;
      const max = Math.max(1, (doc.scrollHeight || 0) - window.innerHeight);
      return (window.scrollY || 0) / max >= 0.4;
    };
    const tryFire = () => {
      if (fired || !armed || !elapsed) return;
      fired = true;
      fireTimer();
    };
    const onScroll = () => { if (!armed && scrolledEnough()) { armed = true; tryFire(); } };
    window.addEventListener("scroll", onScroll, { passive: true });
    const dwell = setTimeout(() => { elapsed = true; tryFire(); }, 45000);
    const fireTimer = () => {
      // BUG FIX (Jul 3, 2026 — Fast Track 0% conversion RCA):
      // The booking modal (`InlineBookingFlow`) opens OVER the home page,
      // so the URL path stays "/" and this timer keeps ticking. When it
      // fires, the popup (z-[9999]) covers the booking modal (z-[200])
      // — stealing users mid-Fast-Track. Skip if the booking modal is
      // in the DOM at fire time, and skip if the booking modal opens
      // AFTER we're shown (dismissed via the isBookingOpen watcher below).
      if (typeof document !== "undefined" &&
          document.querySelector('[data-testid="booking-flow-modal"]')) {
        return;
      }
      if (typeof window !== "undefined" && window.__klrnBookingOpen) return;
      // BUG FIX (Feb 2026): also suppress if ANY Radix Dialog is open
      // (LMC modal, ContactModal, membership modals, etc.) — otherwise
      // we render on top but Radix's inert-siblings behaviour freezes
      // our X and backdrop, locking the client on the page.
      // BUG FIX (Feb 2026 · iter 370): also detect the AI Skin Analysis
      // modal — it's a plain motion.div (not Radix) at z-[200] and this
      // popup at z-[max-int] used to swallow all clicks inside it.
      if (typeof document !== "undefined" && (
          document.querySelector('[role="dialog"][data-state="open"]') ||
          document.querySelector('[data-testid="contact-modal"]') ||
          document.querySelector('[data-testid="last-minute-modal"]') ||
          document.querySelector('[data-testid="ai-skin-analysis-modal"]') ||
          document.querySelector('[data-testid="lumi-fab-modal"]')
      )) {
        return;
      }
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* noop */ }
      setShow(true);
      setTimeout(() => setAnimate(true), 50);
    };
    return () => { clearTimeout(dwell); window.removeEventListener("scroll", onScroll); };
  }, []);

  // If the popup is already visible and the user opens the booking modal
  // (e.g., timer fired at 18 s while they were reading, they click Book Now
  // right after), dismiss silently so we don't cover the funnel.
  // Feb 2026 · extended to also dismiss when any Radix Dialog opens after
  // us — same "inert siblings" bug applies mid-flight.
  useEffect(() => {
    if (!show) return;
    const check = setInterval(() => {
      if (typeof document === "undefined") return;
      const bookingOpen = !!document.querySelector('[data-testid="booking-flow-modal"]')
        || (typeof window !== "undefined" && !!window.__klrnBookingOpen);
      const radixOpen = !!document.querySelector('[role="dialog"][data-state="open"]')
        || !!document.querySelector('[data-testid="contact-modal"]')
        || !!document.querySelector('[data-testid="last-minute-modal"]')
        || !!document.querySelector('[data-testid="ai-skin-analysis-modal"]')
        || !!document.querySelector('[data-testid="lumi-fab-modal"]');
      if (bookingOpen || radixOpen) {
        // Silent dismiss — DON'T persist to STORAGE_KEY so this visitor
        // can still see the offer on their next visit if they don't book.
        setAnimate(false);
        setTimeout(() => setShow(false), 300);
      }
    }, 400);
    return () => clearInterval(check);
  }, [show]);

  // Escape key = dismiss. Belt-and-braces for the rare case where the
  // Radix inert layer sneaks through despite the guards above.
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show]);

  const dismiss = () => {
    setAnimate(false);
    setTimeout(() => {
      setShow(false);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }, 300);
  };

  const handleSignUp = () => {
    dismiss();
    setTimeout(() => navigate("/my-appointments"), 350);
  };

  if (!show) return null;

  // Portal to <body> so we escape any parent stacking / inert contexts.
  // Without this, Radix UI Dialog components elsewhere on the page mark
  // all their siblings as inert when open — which silently freezes this
  // popup's X and backdrop (see the LMC "trapped clients" bug, Feb 2026).
  return createPortal(
    <div
      className={`fixed inset-0 z-[2147483001] flex items-center justify-center p-4 transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"}`}
      onClick={dismiss}
      data-testid="welcome-popup-overlay"
      style={{ touchAction: "manipulation" }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

      <div
        className={`relative w-full max-w-md transition-all duration-500 ${animate ? "scale-100 translate-y-0" : "scale-95 translate-y-4"}`}
        onClick={(e) => e.stopPropagation()}
        data-testid="welcome-popup"
      >
        {/* Animated shimmer border — light chaser effect */}
        <div className="absolute -inset-[2px] rounded-2xl overflow-hidden">
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              background: "conic-gradient(from var(--shimmer-angle, 0deg), transparent 0%, #d4a574 10%, #f5d5a0 20%, #d4a574 30%, transparent 40%, transparent 60%, #d4a574 70%, #f5d5a0 80%, #d4a574 90%, transparent 100%)",
              animation: "shimmerSpin 3s linear infinite",
            }}
          />
          {/* Inner mask to create border effect */}
          <div className="absolute inset-[2px] rounded-[14px] bg-[#0a0a0a]" />
        </div>

        {/* Outer glow */}
        <div className="absolute -inset-4 rounded-3xl bg-amber-500/10 blur-xl pointer-events-none" />
        <div className="absolute -inset-2 rounded-2xl bg-amber-400/5 blur-md pointer-events-none" />

        {/* Main card — glassmorphism */}
        <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(145deg, rgba(15,12,8,0.95) 0%, rgba(25,18,10,0.92) 40%, rgba(15,12,8,0.95) 100%)" }}>
          {/* Subtle noise/grain texture overlay */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")" }} />

          {/* Glass reflection line at top */}
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" />
          <div className="absolute top-[1px] left-[10%] right-[10%] h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          {/* Close button */}
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full border border-amber-500/20 bg-black/40 hover:bg-amber-500/10 backdrop-blur-sm transition-all group"
            data-testid="welcome-popup-close"
          >
            <X className="w-3.5 h-3.5 text-amber-400/60 group-hover:text-amber-300 transition-colors" />
          </button>

          {/* Content */}
          <div className="relative px-6 sm:px-8 pt-10 pb-7 text-center">
            {/* Floating particles (decorative) */}
            <div className="absolute top-6 left-8 w-1 h-1 rounded-full bg-amber-400/40 animate-pulse" style={{ animationDelay: "0s" }} />
            <div className="absolute top-12 right-12 w-0.5 h-0.5 rounded-full bg-amber-300/50 animate-pulse" style={{ animationDelay: "1s" }} />
            <div className="absolute top-20 left-16 w-0.5 h-0.5 rounded-full bg-amber-400/30 animate-pulse" style={{ animationDelay: "0.5s" }} />

            {/* Icon — glowing gift */}
            <div className="relative mx-auto w-20 h-20 sm:w-24 sm:h-24 mb-6">
              <div className="absolute inset-0 rounded-full bg-amber-500/15 blur-xl animate-pulse" />
              <div className="absolute inset-1 rounded-full bg-gradient-to-br from-amber-500/10 to-transparent" />
              <div className="relative w-full h-full rounded-full border border-amber-500/30 flex items-center justify-center" style={{ background: "radial-gradient(circle at 30% 30%, rgba(212,165,116,0.15) 0%, transparent 70%)" }}>
                <Gift className="w-9 h-9 sm:w-11 sm:h-11 text-amber-400" style={{ filter: "drop-shadow(0 0 8px rgba(212,165,116,0.4))" }} />
              </div>
            </div>

            {/* Welcome badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ background: "linear-gradient(135deg, rgba(212,165,116,0.12) 0%, rgba(212,165,116,0.06) 100%)", border: "1px solid rgba(212,165,116,0.25)" }}>
              <Sparkles className="w-3 h-3 text-amber-400" style={{ filter: "drop-shadow(0 0 4px rgba(212,165,116,0.5))" }} />
              <span className="text-[10px] sm:text-xs font-semibold text-amber-400 uppercase tracking-[0.2em]">
                {isFr ? "Offre de bienvenue" : "Welcome Offer"}
              </span>
              <Sparkles className="w-3 h-3 text-amber-400" style={{ filter: "drop-shadow(0 0 4px rgba(212,165,116,0.5))" }} />
            </div>

            {/* Headline */}
            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isFr ? "Obtenez" : "Get"}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-amber-300" style={{ filter: "drop-shadow(0 0 12px rgba(212,165,116,0.3))" }}>$60</span>{" "}
              {isFr ? "instantanément" : "Instantly"}
            </h2>

            <p className="text-amber-100/40 text-sm sm:text-base mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isFr ? "Inscrivez-vous au programme" : "Sign up for Karine Lauzon RN"}
            </p>
            <p className="text-amber-100/40 text-sm sm:text-base mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isFr ? "Crown Reserve de Karine Lauzon RN" : "Crown Reserve Rewards"}
            </p>

            {/* Value box — glass panel */}
            <div className="relative mx-auto max-w-[300px] rounded-xl px-5 py-3.5 mb-7 overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(212,165,116,0.08) 0%, rgba(212,165,116,0.03) 100%)", border: "1px solid rgba(212,165,116,0.18)" }}>
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-400/20 to-transparent" />
              <p className="text-amber-300/80 text-xs sm:text-sm font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isFr
                  ? "Inscrivez-vous et nous mettrons immédiatement 60$ en crédit sur votre compte"
                  : "Sign up and we'll immediately put a $60 credit on your account"}
              </p>
            </div>

            {/* CTA — golden metallic button */}
            <button
              onClick={handleSignUp}
              className="relative w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-10 py-4 rounded-full font-bold text-sm sm:text-base tracking-wide transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] overflow-hidden group"
              style={{
                background: "linear-gradient(135deg, #c9953c 0%, #e8c066 25%, #d4a54a 50%, #c9953c 75%, #b8862e 100%)",
                boxShadow: "0 4px 24px rgba(212,165,116,0.3), inset 0 1px 0 rgba(255,255,255,0.2)",
                color: "#1a0f00",
              }}
              data-testid="welcome-popup-signup-btn"
            >
              {/* Shimmer sweep on button */}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isFr ? "S'inscrire maintenant" : "Sign Up Now"}
              </span>
              <ArrowRight className="w-4 h-4 relative" />
            </button>

            {/* Dismiss */}
            <button
              onClick={dismiss}
              className="mt-5 text-amber-100/25 hover:text-amber-100/50 text-xs transition-colors block mx-auto"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="welcome-popup-dismiss"
            >
              {isFr ? "Non merci, peut-être plus tard" : "No thanks, maybe later"}
            </button>

            {/* Escape hatch — let booking-intent users reach the booking
                flow in one tap. Added Jun 22, 2026 after several clients
                texted Karine saying they "couldn't book". */}
            <button
              onClick={() => {
                dismiss();
                setTimeout(() => navigate("/book"), 350);
              }}
              className="mt-2 text-amber-300/70 hover:text-amber-200 text-xs underline underline-offset-2 transition-colors block mx-auto"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="welcome-popup-book-instead"
            >
              {isFr ? "Je veux d'abord prendre rendez-vous →" : "I just want to book an appointment →"}
            </button>
          </div>

          {/* Bottom glass reflection */}
          <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-amber-500/15 to-transparent" />
        </div>
      </div>

      {/* CSS for the spinning shimmer border */}
      <style>{`
        @property --shimmer-angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes shimmerSpin {
          from { --shimmer-angle: 0deg; transform: rotate(0deg); }
          to { --shimmer-angle: 360deg; transform: rotate(360deg); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default WelcomeRewardsPopup;
